import React, { useState } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { formatCurrency, formatDateForInput } from '../utils/helpers';
import Modal from './Modal';

function PeopleManager() {
  const { 
    state, 
    addPerson, 
    updatePerson, 
    deletePerson,
    addLending,
    settleLending,
    addBorrowing,
    settleBorrowing
  } = useFinance();
  
  const [activeTab, setActiveTab] = useState('people'); // people, lending, borrowing
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('person'); // person, lend, borrow, settle-lend, settle-borrow
  const [selectedItem, setSelectedItem] = useState(null); // person or record to edit/settle

  // Forms
  const [personForm, setPersonForm] = useState({ name: '', phone: '', notes: '' });
  const [recordForm, setRecordForm] = useState({ 
    personId: '', 
    amount: '', 
    description: '', 
    date: formatDateForInput(new Date().toISOString()),
    modeId: state.modes[0]?.id || ''
  });
  const [settleForm, setSettleForm] = useState({
    amount: '',
    modeId: state.modes[0]?.id || ''
  });

  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedItem(item);
    
    if (type === 'person') {
      setPersonForm(item ? { name: item.name, phone: item.phone, notes: item.notes } : { name: '', phone: '', notes: '' });
    } else if (type === 'lend' || type === 'borrow') {
      const defaultMode = state.modes[0]?.id || '';
      if (item) { // Pre-fill person if triggered from person card? (Not implemented here, but could be)
         setRecordForm({ personId: item.id, amount: '', description: '', date: formatDateForInput(new Date().toISOString()), modeId: defaultMode });
      } else {
         setRecordForm({ personId: '', amount: '', description: '', date: formatDateForInput(new Date().toISOString()), modeId: defaultMode });
      }
    } else if (type === 'settle-lend' || type === 'settle-borrow') {
       // Pre-fill remaining amount
       const settled = (item.settlements || []).reduce((acc, s) => acc + s.amount, 0);
       const remaining = item.amount - settled;
       setSettleForm({ amount: remaining.toString(), modeId: state.modes[0]?.id || '' });
    }
    
    setIsModalOpen(true);
  };

  const handlePersonSubmit = (e) => {
    e.preventDefault();
    if (selectedItem) {
      updatePerson({ ...selectedItem, ...personForm });
    } else {
      addPerson(personForm);
    }
    setIsModalOpen(false);
  };

  const handleRecordSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...recordForm,
      amount: parseFloat(recordForm.amount),
      date: new Date(recordForm.date).toISOString()
    };

    if (modalType === 'lend') {
      addLending(data);
    } else {
      addBorrowing(data);
    }
    setIsModalOpen(false);
  };

  const handleSettleSubmit = (e) => {
    e.preventDefault();
    const amount = parseFloat(settleForm.amount);
    if (modalType === 'settle-lend') {
      settleLending(selectedItem.id, amount, settleForm.modeId);
    } else {
      settleBorrowing(selectedItem.id, amount, settleForm.modeId);
    }
    setIsModalOpen(false);
  };

  const getPersonName = (id) => state.people.find(p => p.id === id)?.name || 'Unknown';

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">People & Money</h2>
          <p className="text-slate-400 mt-1">Track who owes you and who you owe</p>
        </div>
        <div className="flex gap-2">
            <button
                onClick={() => openModal('person')}
                className="px-4 py-2 rounded-xl bg-slate-700 text-white font-medium hover:bg-slate-600 transition-all"
            >
                Add Person
            </button>
            <button
                onClick={() => openModal('lend')}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-medium hover:from-emerald-500 hover:to-emerald-400 transition-all"
            >
                Lend Money
            </button>
            <button
                onClick={() => openModal('borrow')}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-rose-600 to-rose-500 text-white font-medium hover:from-rose-500 hover:to-rose-400 transition-all"
            >
                Borrow Money
            </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2 overflow-x-auto">
        {['people', 'lending', 'borrowing'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === tab ? 'bg-primary-500/20 text-primary-400' : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {activeTab === 'people' && state.people.map(person => (
          <div key={person.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center text-xl text-white font-bold">
                {person.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-white font-medium">{person.name}</h3>
                {person.phone && <p className="text-sm text-slate-400">{person.phone}</p>}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => openModal('person', person)} className="flex-1 py-1.5 bg-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-600">Edit</button>
              <button 
                onClick={() => { if(window.confirm('Delete person?')) deletePerson(person.id); }} 
                className="py-1.5 px-3 bg-rose-500/10 text-rose-400 rounded-lg text-sm hover:bg-rose-500/20"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}

        {activeTab === 'lending' && state.lendings.map(lend => {
            const settled = (lend.settlements || []).reduce((acc, s) => acc + s.amount, 0);
            const remaining = lend.amount - settled;
            const progress = (settled / lend.amount) * 100;
            
            return (
              <div key={lend.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 relative overflow-hidden">
                {lend.isSettled && <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs px-2 py-1 rounded-bl-lg">Settled</div>}
                
                <div className="flex justify-between items-start mb-2">
                    <div>
                        <h3 className="text-white font-medium">{getPersonName(lend.personId)}</h3>
                        <p className="text-sm text-slate-400">{lend.description || 'Lent Money'}</p>
                    </div>
                     <span className="text-emerald-400 font-bold">{formatCurrency(lend.amount, state.currency)}</span>
                </div>
                
                <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                        <span>Paid: {formatCurrency(settled, state.currency)}</span>
                        <span>Due: {formatCurrency(remaining, state.currency)}</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>

                {!lend.isSettled && (
                    <button 
                        onClick={() => openModal('settle-lend', lend)}
                        className="w-full mt-3 py-2 rounded-lg bg-emerald-600/20 text-emerald-400 text-sm font-medium hover:bg-emerald-600/30 transition-colors"
                    >
                        Mark Payment Received
                    </button>
                )}
              </div>
            );
        })}

        {activeTab === 'borrowing' && state.borrowings.map(borrow => {
             const settled = (borrow.settlements || []).reduce((acc, s) => acc + s.amount, 0);
             const remaining = borrow.amount - settled;
             const progress = (settled / borrow.amount) * 100;

             return (
              <div key={borrow.id} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 relative overflow-hidden">
                {borrow.isSettled && <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-2 py-1 rounded-bl-lg">Settled</div>}
                
                <div className="flex justify-between items-start mb-2">
                    <div>
                        <h3 className="text-white font-medium">{getPersonName(borrow.personId)}</h3>
                        <p className="text-sm text-slate-400">{borrow.description || 'Borrowed Money'}</p>
                    </div>
                     <span className="text-rose-400 font-bold">{formatCurrency(borrow.amount, state.currency)}</span>
                </div>

                <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                        <span>Paid: {formatCurrency(settled, state.currency)}</span>
                        <span>Due: {formatCurrency(remaining, state.currency)}</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-rose-500" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>

                {!borrow.isSettled && (
                    <button 
                        onClick={() => openModal('settle-borrow', borrow)}
                        className="w-full mt-3 py-2 rounded-lg bg-rose-600/20 text-rose-400 text-sm font-medium hover:bg-rose-600/30 transition-colors"
                    >
                        Make Repayment
                    </button>
                )}
              </div>
             );
        })}
      </div>

      {/* Modal Logic */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={
            modalType === 'person' ? (selectedItem ? 'Edit Person' : 'Add Person') :
            modalType === 'lend' ? 'Lend Money' :
            modalType === 'borrow' ? 'Borrow Money' :
            modalType === 'settle-lend' ? 'Record Payment Info' :
            'Record Repayment Info'
        }
      >
          {modalType === 'person' && (
              <form onSubmit={handlePersonSubmit} className="space-y-4">
                  <div>
                      <label className="block text-sm text-slate-300 mb-1">Name</label>
                      <input type="text" value={personForm.name} onChange={e => setPersonForm({...personForm, name: e.target.value})} required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                  </div>
                  <div>
                      <label className="block text-sm text-slate-300 mb-1">Phone (Optional)</label>
                      <input type="text" value={personForm.phone} onChange={e => setPersonForm({...personForm, phone: e.target.value})} className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                  </div>
                  <button type="submit" className="w-full py-3 bg-primary-600 text-white rounded-xl font-medium">Save Person</button>
              </form>
          )}

          {(modalType === 'lend' || modalType === 'borrow') && (
              <form onSubmit={handleRecordSubmit} className="space-y-4">
                   <div>
                      <label className="block text-sm text-slate-300 mb-1">Person</label>
                      <select value={recordForm.personId} onChange={e => setRecordForm({...recordForm, personId: e.target.value})} required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white">
                          <option value="">Select Person</option>
                          {state.people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                  </div>
                  <div>
                      <label className="block text-sm text-slate-300 mb-1">Amount</label>
                      <input type="number" step="0.01" value={recordForm.amount} onChange={e => setRecordForm({...recordForm, amount: e.target.value})} required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                  </div>
                  <div>
                      <label className="block text-sm text-slate-300 mb-1">Payment Mode</label>
                      <select value={recordForm.modeId} onChange={e => setRecordForm({...recordForm, modeId: e.target.value})} required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white">
                          {state.modes.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                  </div>
                   <div>
                      <label className="block text-sm text-slate-300 mb-1">Description</label>
                      <input type="text" value={recordForm.description} onChange={e => setRecordForm({...recordForm, description: e.target.value})} className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                  </div>
                   <div>
                      <label className="block text-sm text-slate-300 mb-1">Date</label>
                      <input type="date" value={recordForm.date} onChange={e => setRecordForm({...recordForm, date: e.target.value})} required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                  </div>
                  <button type="submit" className="w-full py-3 bg-primary-600 text-white rounded-xl font-medium">Save Record</button>
              </form>
          )}

          {(modalType === 'settle-lend' || modalType === 'settle-borrow') && (
              <form onSubmit={handleSettleSubmit} className="space-y-4">
                  <p className="text-slate-300 text-sm">
                      Recording {modalType === 'settle-lend' ? 'payment from' : 'repayment to'} <span className="text-white font-bold">{getPersonName(selectedItem?.personId)}</span>
                  </p>
                  <div>
                      <label className="block text-sm text-slate-300 mb-1">Amount</label>
                      <input type="number" step="0.01" value={settleForm.amount} onChange={e => setSettleForm({...settleForm, amount: e.target.value})} required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white" />
                  </div>
                  <div>
                      <label className="block text-sm text-slate-300 mb-1">Payment Mode</label>
                      <select value={settleForm.modeId} onChange={e => setSettleForm({...settleForm, modeId: e.target.value})} required className="w-full px-4 py-2 bg-slate-700 rounded-lg text-white">
                          {state.modes.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                  </div>
                  <button type="submit" className="w-full py-3 bg-primary-600 text-white rounded-xl font-medium">Record Transaction</button>
              </form>
          )}
      </Modal>
    </div>
  );
}

export default PeopleManager;
